import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import StandardInputField from '../../Components/StandardComponents/StandardInputField/StandardInputField';
import { localDate, toYmd } from '../../Components/KnowledgeLibrary/knowledgeLibraryConfig';
import KnowledgeDropZone from './KnowledgeDropZone';
import { POLL_LIMIT, POLL_MS, indexFiles } from './knowledgeDropIn';
import { amendmentDetails, amendmentExtra, familyOf, newVersionDetails, versionExtra } from './knowledgeHelpers';

const FIELD = { uiStyle: 'field_styling_25', fullWidth: true };

/**
 * "Add new version" or "Add amendment" on a law row. A new version keeps the family, title, tier
 * and industries of the current version and needs a version label and an effective-from date. An
 * amendment points at the law's family and needs its commencement date. Both drop one file and go
 * through the same upload, create and index steps as any drop-in.
 */
export default function KnowledgeVersionDialog({ open, mode, row, onClose, onDone, pollMs = POLL_MS, pollLimit = POLL_LIMIT }) {
  const isVersion = mode === 'version';
  const [entry, setEntry] = useState(null);
  const [fields, setFields] = useState({ title: '', versionLabel: '', effectiveFrom: '', commencementDate: '' });
  const [problems, setProblems] = useState([]);
  const [running, setRunning] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    if (open) {
      setEntry(null);
      setFields({ title: '', versionLabel: '', effectiveFrom: '', commencementDate: '' });
      setProblems([]);
      setRunning(false);
    }
  }, [open, mode, row]);

  if (!row) return null;
  const set = (name, value) => setFields((f) => ({ ...f, [name]: value }));
  const onDate = (e) => set(e.target.name, e.target.value instanceof Date ? toYmd(e.target.value) : (e.target.value || ''));
  const done = entry && (entry.state === 'indexed' || entry.state === 'error' || entry.state === 'indexing') && !running;

  const submit = async () => {
    const found = [];
    if (!entry?.file) found.push(isVersion ? 'Drop the new version of the law' : 'Drop the amending Act');
    if (isVersion && !fields.versionLabel.trim()) found.push('Version label is required');
    if (isVersion && !fields.effectiveFrom) found.push('Effective from is required');
    if (!isVersion && !fields.commencementDate) found.push('Commencement date is required');
    setProblems(found);
    if (found.length) return;
    setRunning(true);
    const details = isVersion ? newVersionDetails(row) : { ...amendmentDetails(row), title: fields.title };
    const extra = isVersion
      ? versionExtra(row, { versionLabel: fields.versionLabel, effectiveFrom: fields.effectiveFrom })
      : amendmentExtra(row, { commencementDate: fields.commencementDate });
    await indexFiles({
      files: [entry.file],
      details,
      extraFor: () => extra,
      onUpdate: (i, patch) => { if (aliveRef.current) setEntry((e) => ({ ...e, ...patch })); },
      pollMs,
      pollLimit,
      alive: () => aliveRef.current,
    });
    if (!aliveRef.current) return;
    setRunning(false);
    if (onDone) onDone();
  };

  const title = isVersion ? `Add a new version of ${row.title}` : `Add an amendment to ${row.title}`;
  return (
    <Dialog open={open} onClose={running ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="knowledge-version-title">
      <DialogTitle id="knowledge-version-title">{title}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: '#64748B', mb: 2 }}>
          {isVersion
            ? `Family ${familyOf(row)}. The title, tier and industries come from the current version. Publishing it supersedes the version in force.`
            : `Amends ${familyOf(row)}. Until its commencement date the amendment is shown as pending commencement.`}
        </Typography>
        {problems.length ? <Alert severity="error" role="alert" sx={{ mb: 2 }}>{problems.join('. ')}.</Alert> : null}
        {isVersion ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1.5 }}>
            <StandardInputField type="text" name="versionLabel" label="Version label" required value={fields.versionLabel} disabled={running}
              onChange={(e) => set('versionLabel', e.target.value)} config={{ ...FIELD, placeholder: 'Revised Edition 2020' }} />
            <StandardInputField type="date" name="effectiveFrom" label="Effective from" required value={localDate(fields.effectiveFrom)} disabled={running}
              onChange={onDate} config={{ ...FIELD, formatPattern: 'yyyy-MM-dd' }} />
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1.5 }}>
            <StandardInputField type="text" name="title" label="Title" value={fields.title} disabled={running}
              onChange={(e) => set('title', e.target.value)} helperText="Leave blank to use the file name" config={{ ...FIELD, placeholder: 'PDPA (Amendment) Act 2020' }} />
            <StandardInputField type="date" name="commencementDate" label="Commencement date" required value={localDate(fields.commencementDate)} disabled={running}
              onChange={onDate} config={{ ...FIELD, formatPattern: 'yyyy-MM-dd' }} />
          </Box>
        )}
        <KnowledgeDropZone
          multiple={false}
          disabled={running || done}
          label={isVersion ? 'Drop the new version here' : 'Drop the amending Act here'}
          entries={entry ? [entry] : []}
          onAdd={(files) => setEntry({ file: files[0], state: 'queued', message: '' })}
          onRemove={() => setEntry(null)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running} sx={{ textTransform: 'none' }}>{done ? 'Close' : 'Cancel'}</Button>
        {!done ? (
          <Button variant="contained" onClick={submit} disabled={running} sx={{ textTransform: 'none' }}>
            {isVersion ? 'Add version' : 'Add amendment'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
